import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lock,
  LogOut,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Image as ImageIcon,
  ExternalLink,
  RefreshCw,
  Inbox,
  Briefcase,
  Save,
  Mail,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  UploadCloud,
} from 'lucide-react';
import useSEO from '../hooks/useSEO';
import { CIRCLE_CATEGORIES } from '../content/circleCategories';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'casanova.admin.key';

const statusMeta = {
  pending: { label: 'En attente', color: '#A14223', bg: 'rgba(161,66,35,0.08)' },
  approved: { label: 'Approuvé', color: '#25342C', bg: 'rgba(37,52,44,0.10)' },
  rejected: { label: 'Refusé', color: '#8A8780', bg: 'rgba(138,135,128,0.12)' },
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-CA', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

// Automated-email performance: 24h reminders + abandoned-booking recovery.
const EmailStatsCard = ({ authHeaders }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancel = false;
    axios
      .get(`${API}/admin/email-stats`, authHeaders)
      .then(({ data }) => { if (!cancel) setStats(data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [authHeaders]);

  if (!stats) return null;
  const items = [
    { key: 'reminders', label: 'Rappels 24h envoyés', value: stats.reminders.sent },
    { key: 'recovery-sent', label: 'Relances envoyées', value: stats.recovery.emails_sent },
    {
      key: 'recovered',
      label: 'Réservations récupérées',
      value: stats.recovery.recovered,
      detail: stats.recovery.emails_sent ? `${stats.recovery.conversion_rate}% de conversion` : null,
      highlight: true,
    },
  ];

  return (
    <section className="max-w-6xl mx-auto px-4 lg:px-8 mt-8" data-testid="admin-email-stats">
      <div className="bg-white border border-[#8A8780]/25 px-5 py-4 flex flex-wrap items-center gap-x-10 gap-y-3">
        <div className="flex items-center gap-2 text-[#1A1A18]">
          <Mail size={14} className="text-[#A14223]" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">Emails automatisés</span>
        </div>
        {items.map((it) => (
          <div key={it.key} className="flex items-baseline gap-2" data-testid={`admin-email-stat-${it.key}`}>
            <span className={`font-display text-2xl leading-none ${it.highlight ? 'text-[#A14223]' : 'text-[#1A1A18]'}`}>
              {it.value}
            </span>
            <span className="font-body text-xs text-[#1A1A18]/65">
              {it.label}
              {it.detail && <span className="text-[#8A8780]"> · {it.detail}</span>}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

const AdminClientelePage = () => {
  useSEO({
    title: 'Admin — Le Cercle',
    description: 'Casanova admin — Le Cercle review panel.',
  });

  const [apiKey, setApiKey] = useState(() => sessionStorage.getItem(STORAGE_KEY) || '');
  const [keyInput, setKeyInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [requests, setRequests] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${apiKey}` } }),
    [apiKey]
  );

  const load = useCallback(async () => {
    if (!apiKey) return;
    setLoading(true);
    setAuthError('');
    try {
      const { data } = await axios.get(`${API}/admin/clientele/requests`, authHeaders);
      data.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setRequests(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setApiKey('');
        sessionStorage.removeItem(STORAGE_KEY);
        setAuthError('Invalid admin key.');
      } else {
        setAuthError(err?.response?.data?.detail || 'Could not load requests.');
      }
    } finally {
      setLoading(false);
    }
  }, [apiKey, authHeaders]);

  useEffect(() => {
    if (apiKey) load();
  }, [apiKey, load]);

  const onLogin = (e) => {
    e.preventDefault();
    const k = keyInput.trim();
    if (!k) return;
    sessionStorage.setItem(STORAGE_KEY, k);
    setApiKey(k);
    setKeyInput('');
  };

  const onLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setApiKey('');
    setRequests(null);
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const patchRequest = async (id, patch, successMsg) => {
    try {
      const { data } = await axios.patch(`${API}/admin/clientele/${id}`, patch, authHeaders);
      // Replace the row in state instead of reloading the whole list.
      setRequests((rows) =>
        (rows || []).map((r) => (r.id === id ? { ...r, ...data } : r))
      );
      showToast(successMsg || 'Saved.');
      return data;
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Action failed.');
      throw err;
    }
  };

  // ---- Login screen ----
  if (!apiKey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F0E8] px-6" data-testid="admin-login">
        <motion.form
          onSubmit={onLogin}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative bg-white border border-[#8A8780]/25 p-8 lg:p-10 max-w-md w-full"
        >
          <span aria-hidden className="absolute left-0 top-0 h-[2px] w-16 bg-[#A14223]" />
          <Lock size={20} className="text-[#A14223] mb-5" />
          <h1 className="font-display text-2xl lg:text-3xl text-[#1A1A18] mb-2">Admin · Casanova</h1>
          <p className="font-body text-sm text-[#1A1A18]/65 mb-6">
            Enter your admin key to review Le Cercle applications.
          </p>
          <input
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="Admin key"
            required
            autoFocus
            autoComplete="current-password"
            className="w-full px-4 py-3 bg-[#F5F0E8] border border-[#8A8780]/30 font-body text-sm text-[#1A1A18] focus:outline-none focus:border-[#A14223]"
            data-testid="admin-key-input"
          />
          {authError && (
            <p className="mt-3 font-body text-xs text-[#A14223]" data-testid="admin-login-error">
              {authError}
            </p>
          )}
          <button type="submit" className="mt-6 btn-cta w-full" data-testid="admin-login-submit">
            Enter
          </button>
          <p className="mt-6 text-[11px] text-[#8A8780] font-body leading-relaxed">
            The key is stored only for this browser session.
          </p>
        </motion.form>
      </div>
    );
  }

  // ---- Dashboard ----
  const filtered =
    filter === 'all'
      ? (requests || [])
      : (requests || []).filter((r) => r.status === filter);

  const counts = {
    pending: (requests || []).filter((r) => r.status === 'pending').length,
    approved: (requests || []).filter((r) => r.status === 'approved').length,
    rejected: (requests || []).filter((r) => r.status === 'rejected').length,
  };

  return (
    <div className="min-h-screen bg-[#F5F0E8] pb-20" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 bg-[#1A1A18] text-[#F5F0E8] border-b border-[#F5F0E8]/10">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Briefcase size={16} className="text-[#A14223]" />
            <div>
              <div className="font-display text-base lg:text-lg leading-none">Admin · Le Cercle</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5F0E8]/55 mt-0.5">
                Casanova Salon Barbier
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F5F0E8]/80 hover:text-[#F5F0E8] px-3 py-2 border border-[#F5F0E8]/15"
              data-testid="admin-refresh"
              disabled={loading}
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">{loading ? 'Loading' : 'Refresh'}</span>
            </button>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F5F0E8]/80 hover:text-[#F5F0E8] px-3 py-2 border border-[#F5F0E8]/15"
              data-testid="admin-logout"
            >
              <LogOut size={12} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <EmailStatsCard authHeaders={authHeaders} />

      {/* Filter tabs */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 mt-8">
        <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
          {[
            { key: 'pending', label: `Pending (${counts.pending})` },
            { key: 'approved', label: `Approved (${counts.approved})` },
            { key: 'rejected', label: `Rejected (${counts.rejected})` },
            { key: 'all', label: `All (${(requests || []).length})` },
          ].map((tab) => {
            const active = filter === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                  active
                    ? 'bg-[#25342C] text-[#F5F0E8] border-[#25342C]'
                    : 'bg-white text-[#1A1A18] border-[#8A8780]/25 hover:border-[#25342C]'
                }`}
                data-testid={`admin-tab-${tab.key}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* List */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 mt-6" data-testid="admin-list">
        {!requests ? (
          <p className="text-[#8A8780] font-body text-sm">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="mt-10 py-16 text-center border border-dashed border-[#8A8780]/30 bg-white" data-testid="admin-empty">
            <Inbox size={28} className="mx-auto text-[#8A8780] mb-4" />
            <p className="font-display italic text-lg text-[#1A1A18]/65">No requests in this view.</p>
          </div>
        ) : (
          <div className="space-y-4 lg:space-y-5">
            {filtered.map((r) => (
              <RequestCard key={r.id} request={r} onPatch={patchRequest} />
            ))}
          </div>
        )}
      </section>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#25342C] text-[#F5F0E8] px-5 py-3 shadow-lg"
            data-testid="admin-toast"
          >
            <span className="font-body text-sm">{toast}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// =====================================================================
// REQUEST CARD — handles per-row state (approve/reject for pending,
// inline profile editor for approved).
// =====================================================================
const RequestCard = ({ request: r, onPatch }) => {
  const meta = statusMeta[r.status] || statusMeta.pending;
  const isPending = r.status === 'pending';
  const isApproved = r.status === 'approved';
  // Profile editor open by default for approved rows that aren't live yet.
  const [editorOpen, setEditorOpen] = useState(isApproved && !r.is_live);

  const approve = () => onPatch(r.id, { status: 'approved' }, 'Approved · welcome email sent.');
  const reject = () => onPatch(r.id, { status: 'rejected' }, 'Rejected.');
  const reset = () => onPatch(r.id, { status: 'pending', is_live: false }, 'Reset to pending.');

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="relative bg-white border border-[#8A8780]/25"
      data-testid={`admin-request-${r.id}`}
    >
      <span aria-hidden className="absolute left-0 top-0 h-full w-[3px]" style={{ background: meta.color }} />

      {/* Header row */}
      <div className="p-5 lg:p-7 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span
              className="inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-[0.22em]"
              style={{ color: meta.color, background: meta.bg }}
            >
              {meta.label}
            </span>
            {r.is_live && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] bg-[#A14223] text-[#F5F0E8]" data-testid={`admin-live-${r.id}`}>
                <Eye size={10} /> Live
              </span>
            )}
            <span className="text-[11px] uppercase tracking-wider text-[#8A8780]">
              {formatDate(r.created_at)}
            </span>
          </div>
          <h2 className="font-display text-2xl lg:text-3xl text-[#1A1A18] leading-tight mb-1">
            {r.full_name}
          </h2>
          <p className="font-body text-sm text-[#1A1A18]/70 mb-1">
            <span className="text-[#8A8780]">Profession : </span>
            {r.profession}
          </p>
          {r.email && (
            <p className="font-body text-sm text-[#1A1A18]/70 mb-1">
              <span className="text-[#8A8780]">Courriel : </span>
              <a href={`mailto:${r.email}`} className="text-[#A14223] hover:underline">{r.email}</a>
            </p>
          )}
          <p className="font-body text-sm text-[#1A1A18]/70 mb-2">
            <span className="text-[#8A8780]">Spécialité : </span>
            {r.specialty}
          </p>
          {r.social_link && (
            <a
              href={r.social_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[#A14223] hover:text-[#8a3519] border-b border-transparent hover:border-[#A14223]"
            >
              {r.social_link.replace(/^https?:\/\//, '').slice(0, 50)}
              <ExternalLink size={11} />
            </a>
          )}
        </div>

        {/* Top-right actions */}
        <div className="flex flex-wrap gap-2 shrink-0">
          {isPending && (
            <>
              <button
                onClick={approve}
                className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] bg-[#25342C] text-[#F5F0E8] hover:bg-[#1f2b24] transition-colors"
                data-testid={`admin-approve-${r.id}`}
              >
                <CheckCircle2 size={13} />
                Approve
              </button>
              <button
                onClick={reject}
                className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] bg-white text-[#1A1A18] border border-[#8A8780]/30 hover:border-[#A14223] hover:text-[#A14223] transition-colors"
                data-testid={`admin-reject-${r.id}`}
              >
                <XCircle size={13} />
                Reject
              </button>
            </>
          )}
          {!isPending && (
            <button
              onClick={reset}
              className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] bg-white text-[#8A8780] border border-[#8A8780]/30 hover:text-[#1A1A18] hover:border-[#1A1A18] transition-colors"
              data-testid={`admin-reset-${r.id}`}
            >
              <RotateCcw size={13} />
              Reset to pending
            </button>
          )}
        </div>
      </div>

      {/* Profile editor — approved members only */}
      {isApproved && (
        <>
          <button
            type="button"
            onClick={() => setEditorOpen((v) => !v)}
            className="w-full flex items-center justify-between px-5 lg:px-7 py-3 bg-[#F5F0E8] border-t border-[#8A8780]/20 text-left"
            data-testid={`admin-toggle-editor-${r.id}`}
          >
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#25342C] font-medium">
              {editorOpen ? 'Profile builder' : 'Open profile builder'}
            </span>
            {editorOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <AnimatePresence initial={false}>
            {editorOpen && (
              <motion.div
                key="editor"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                className="overflow-hidden border-t border-[#8A8780]/20"
              >
                <ProfileEditor request={r} onSave={onPatch} />
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </motion.article>
  );
};

// =====================================================================
// PROFILE EDITOR — 4 questionnaire sections + photo + publish toggle
// =====================================================================
const FIELD_DEFS = [
  { key: 'display_name',         label: 'Display name',                hint: 'Name as shown on the profile.', type: 'text' },
  { key: 'title',                label: 'Title',                       hint: 'One line under their name. ex. Restaurateur · Bar Sociale.', type: 'text' },
  { key: 'category',             label: 'Category',                    hint: 'Professional category the member gets filed under on /cercle.', type: 'select', options: CIRCLE_CATEGORIES, placeholder: '— Choose a category —' },
  { key: 'at_casanova',          label: '01 · At Casanova',            hint: 'Q1 — How long, who cuts their hair, what they love.', type: 'textarea', rows: 4 },
  { key: 'about',                label: '02 · About',                  hint: 'Q2 — Tell us about you.', type: 'textarea', rows: 4 },
  { key: 'profession_specialty', label: '03 · Profession & Specialty', hint: 'Q3 — Profession, expertise, title.', type: 'textarea', rows: 4 },
  { key: 'connect',              label: '04 · Connect',                hint: 'Q4 — Phone, email, Instagram, LinkedIn — one per line.', type: 'textarea', rows: 4 },
  // photo_url is handled separately with a dedicated file-upload UI — it
  // is intentionally NOT in this generic-field list.
];

// ---------------------------------------------------------------------
// Compress an image file on the client so we never POST multi-MB blobs
// to the backend. Draws the image onto a canvas at max MAX_SIDE pixels
// on the long edge, exports as JPEG at QUALITY, and returns the result
// as a data URL (base64). Preserves aspect ratio and auto-corrects
// EXIF orientation where supported (createImageBitmap option).
// ---------------------------------------------------------------------
const MAX_SIDE = 1200;     // long-edge cap — plenty for retina cards
const JPEG_QUALITY = 0.86; // visually lossless for portraits

const compressImageFile = (file) =>
  new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) {
      return reject(new Error('Not an image file'));
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Could not decode image'));
      img.onload = () => {
        const { width: w, height: h } = img;
        const scale = Math.min(1, MAX_SIDE / Math.max(w, h));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#000'; // fallback for PNGs with transparency
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const dataUrlSizeKB = (dataUrl) => {
  if (!dataUrl || !dataUrl.startsWith('data:')) return null;
  // base64 string is 4/3 larger than the raw bytes it encodes
  const base64 = dataUrl.split(',')[1] || '';
  return Math.round((base64.length * 3) / 4 / 1024);
};

const ProfileEditor = ({ request: r, onSave }) => {
  // Local form state — initialised from the request, kept in sync if the
  // request prop changes (e.g. after another save).
  const [form, setForm] = useState(() => buildFormState(r));
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState('');

  useEffect(() => {
    setForm(buildFormState(r));
  }, [r]);

  const dirty = useMemo(() => {
    return Object.keys(form).some((k) => (form[k] || '') !== (r[k] || ''));
  }, [form, r]);

  // Profile is "ready to publish" once it has, at minimum, a display name,
  // a title, a category, all four questionnaire sections, and a photo.
  const readyToPublish = Boolean(
    (form.display_name || r.full_name) &&
      form.title &&
      form.category &&
      form.at_casanova &&
      form.about &&
      form.profession_specialty &&
      form.connect &&
      form.photo_url
  );

  const handleChange = (key) => (e) => {
    setForm((p) => ({ ...p, [key]: e.target.value }));
  };

  const handlePhotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setPhotoError('');
    setPhotoBusy(true);
    try {
      const dataUrl = await compressImageFile(file);
      setForm((p) => ({ ...p, photo_url: dataUrl }));
    } catch (err) {
      setPhotoError(err?.message || 'Could not process image');
    } finally {
      setPhotoBusy(false);
    }
  };

  const clearPhoto = () => {
    setForm((p) => ({ ...p, photo_url: '' }));
    setPhotoError('');
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Always send strings, even empty (so admin can clear a field).
      const patch = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, (v ?? '').trim()])
      );
      await onSave(r.id, patch, 'Profile saved.');
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    // Persist any unsaved edits then flip is_live in a single PATCH.
    const patch = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, (v ?? '').trim()])
    );
    patch.is_live = true;
    await onSave(r.id, patch, 'Profile is now live on /cercle.');
  };

  const unpublish = () => onSave(r.id, { is_live: false }, 'Profile taken offline.');

  return (
    <div className="p-5 lg:p-7 bg-white">
      <p className="text-xs text-[#8A8780] font-body italic mb-5 leading-relaxed">
        Copy each answer from {r.full_name}'s questionnaire reply into the matching field below.
        The submitted intake info above is preserved — these fields populate the public profile.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-5">
        {FIELD_DEFS.map((field) => (
          <div key={field.key} className={field.type === 'textarea' ? 'lg:col-span-2' : ''}>
            <label className="block text-[10px] uppercase tracking-[0.22em] text-[#25342C] mb-1.5">
              {field.label}
            </label>
            {field.type === 'textarea' ? (
              <textarea
                rows={field.rows || 3}
                value={form[field.key]}
                onChange={handleChange(field.key)}
                placeholder={field.hint}
                className="w-full px-3 py-2 bg-[#F5F0E8] border border-[#8A8780]/30 font-body text-sm text-[#1A1A18] focus:outline-none focus:border-[#A14223] resize-none"
                data-testid={`admin-field-${field.key}-${r.id}`}
              />
            ) : field.type === 'select' ? (
              <select
                value={form[field.key]}
                onChange={handleChange(field.key)}
                className="w-full px-3 py-2 bg-[#F5F0E8] border border-[#8A8780]/30 font-body text-sm text-[#1A1A18] focus:outline-none focus:border-[#A14223]"
                data-testid={`admin-field-${field.key}-${r.id}`}
              >
                <option value="">{field.placeholder}</option>
                {(field.options || []).map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={form[field.key]}
                onChange={handleChange(field.key)}
                placeholder={field.hint}
                className="w-full px-3 py-2 bg-[#F5F0E8] border border-[#8A8780]/30 font-body text-sm text-[#1A1A18] focus:outline-none focus:border-[#A14223]"
                data-testid={`admin-field-${field.key}-${r.id}`}
              />
            )}
            <p className="mt-1 text-[10px] text-[#8A8780] font-body">{field.hint}</p>
          </div>
        ))}
      </div>

      {/* ================ PHOTO UPLOADER ================ */}
      <div className="mt-7 pt-6 border-t border-[#8A8780]/20">
        <label className="block text-[10px] uppercase tracking-[0.22em] text-[#25342C] mb-3">
          Profile photo
        </label>
        <div className="flex flex-col sm:flex-row gap-5 sm:items-start">
          {/* Preview */}
          <div className="w-28 h-36 flex-shrink-0 bg-[#F5F0E8] border border-[#8A8780]/30 overflow-hidden relative">
            {form.photo_url ? (
              <img
                src={form.photo_url}
                alt={r.full_name}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                data-testid={`admin-photo-preview-${r.id}`}
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-[#8A8780] gap-1">
                <ImageIcon size={22} />
                <span className="text-[9px] uppercase tracking-[0.2em]">No photo</span>
              </div>
            )}
            {photoBusy && (
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <RefreshCw size={18} className="text-white animate-spin" />
              </div>
            )}
          </div>

          {/* Upload controls */}
          <div className="flex-1 w-full">
            <div className="flex flex-wrap gap-2">
              <label
                className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] bg-[#25342C] text-[#F5F0E8] hover:bg-[#1f2b24] transition-colors cursor-pointer"
                data-testid={`admin-photo-upload-${r.id}`}
              >
                <UploadCloud size={13} />
                {form.photo_url ? 'Replace photo' : 'Upload photo'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoFile}
                  className="hidden"
                  data-testid={`admin-photo-input-${r.id}`}
                />
              </label>
              {form.photo_url && (
                <button
                  type="button"
                  onClick={clearPhoto}
                  className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.2em] bg-white text-[#8A8780] border border-[#8A8780]/30 hover:text-[#A14223] hover:border-[#A14223] transition-colors"
                  data-testid={`admin-photo-clear-${r.id}`}
                >
                  <XCircle size={13} />
                  Remove
                </button>
              )}
            </div>
            <p className="mt-3 text-[11px] text-[#8A8780] font-body leading-relaxed">
              JPG / PNG / WebP. Auto-resized to 1200px long edge, ~100&nbsp;KB. The shot
              is stored directly with the profile — no external hosting needed.
            </p>

            {/* Size badge once an image is present */}
            {form.photo_url && form.photo_url.startsWith('data:') && (
              <p className="mt-1 text-[10px] text-[#8A8780] font-body">
                Size ~{dataUrlSizeKB(form.photo_url)} KB
              </p>
            )}

            {/* Advanced: paste a direct URL instead (collapsed-style text field) */}
            <details className="mt-4 group">
              <summary className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780] cursor-pointer hover:text-[#25342C] select-none">
                Or paste an image URL instead
              </summary>
              <input
                type="text"
                value={form.photo_url && form.photo_url.startsWith('data:') ? '' : form.photo_url}
                onChange={handleChange('photo_url')}
                placeholder="https://…"
                className="mt-2 w-full px-3 py-2 bg-[#F5F0E8] border border-[#8A8780]/30 font-body text-xs text-[#1A1A18] focus:outline-none focus:border-[#A14223]"
                data-testid={`admin-photo-url-${r.id}`}
              />
            </details>

            {photoError && (
              <p className="mt-2 text-xs text-[#A14223]" data-testid={`admin-photo-error-${r.id}`}>
                {photoError}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="mt-7 pt-5 border-t border-[#8A8780]/20 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="text-[11px] text-[#8A8780] font-body">
          {dirty
            ? 'You have unsaved changes.'
            : r.is_live
            ? 'This profile is currently live on /cercle.'
            : readyToPublish
            ? 'Ready to publish.'
            : 'Fill all four sections + display name, title, category, and photo before publishing.'}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] bg-white text-[#1A1A18] border border-[#8A8780]/40 hover:border-[#25342C] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid={`admin-save-${r.id}`}
          >
            <Save size={13} />
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {r.is_live ? (
            <button
              type="button"
              onClick={unpublish}
              className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] bg-[#1A1A18] text-[#F5F0E8] hover:bg-[#000] transition-colors"
              data-testid={`admin-unpublish-${r.id}`}
            >
              <EyeOff size={13} />
              Take offline
            </button>
          ) : (
            <button
              type="button"
              onClick={publish}
              disabled={!readyToPublish}
              className="inline-flex items-center gap-2 px-5 py-2 text-xs uppercase tracking-[0.2em] bg-[#A14223] text-[#F5F0E8] hover:bg-[#8a3519] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid={`admin-publish-${r.id}`}
            >
              <Eye size={13} />
              Make profile live
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Build an editable string-only form state from a request row. Falls back
// to the submitted intake fields where useful (so the editor isn't blank
// for older approved members).
const buildFormState = (r) => ({
  display_name: r.display_name || r.full_name || '',
  title: r.title || r.profession || '',
  category: r.category || '',
  at_casanova: r.at_casanova || '',
  about: r.about || '',
  profession_specialty: r.profession_specialty || '',
  connect: r.connect || '',
  photo_url: r.photo_url || '',
});

export default AdminClientelePage;
